// Calendars stream contracts (module 04: PB-04-10, PB-04-12, PB-04-13, PB-04-14; ADR-022).
import { z } from 'zod';
import type { PreRegistrationView } from './visits.ts';

const Uuid = z.uuid();
const trimmed = (max: number) => z.string().trim().min(1, 'v.required').max(max, 'v.tooLong');
const Reason = trimmed(500);
const Key = z.string().trim().regex(/^[a-z][a-z0-9_-]{0,39}$/, 'v.policyKey');
const Domain = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^(?=.{1,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/, 'v.calendarDomain');
const CalendarRef = z.string().trim().min(1, 'v.required').max(320, 'v.tooLong').regex(/^[^\s<>"']+$/, 'v.calendarRef');

// ------------------------------------------------------------------ connections (PB-04-12, PB-04-14)
export const CALENDAR_PROVIDER_VALUES = ['microsoft', 'google'] as const;
export type CalendarProviderDto = (typeof CALENDAR_PROVIDER_VALUES)[number];

/** Microsoft 365: the Entra directory (tenant) GUID and the application (client) id. */
export const MicrosoftCalendarConfig = z.object({
  directoryTenantId: z.string().trim().toLowerCase().regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/, 'v.calendarGuid'),
  clientId: z.string().trim().min(1, 'v.required').max(200, 'v.tooLong').regex(/^[A-Za-z0-9._-]+$/, 'v.invalid'),
});
/** Google Workspace: the primary domain and the service account used with domain-wide delegation. */
export const GoogleCalendarConfig = z.object({
  workspaceDomain: Domain,
  serviceAccountEmail: z.email('v.email').max(254, 'v.tooLong'),
});

export const CalendarSettingsInput = z.object({
  autoDraft: z.boolean().optional(),
  internalDomains: z.array(Domain).max(20).optional(),
  defaultSiteId: Uuid.nullable().optional(),
  defaultCategoryCode: Key.nullable().optional(),
  matchHostByEmail: z.boolean().optional(),
  horizonDays: z.number().int().min(1).max(366).optional(),
});
export type CalendarSettingsInput = z.infer<typeof CalendarSettingsInput>;

const Secret = z.string().min(8, 'v.secretShort').max(4000, 'v.tooLong');

export const CreateCalendarConnectionRequest = z.discriminatedUnion('provider', [
  z.object({ provider: z.literal('microsoft'), displayName: trimmed(100), config: MicrosoftCalendarConfig, secret: Secret.optional(), settings: CalendarSettingsInput.optional(), reason: Reason }),
  z.object({ provider: z.literal('google'), displayName: trimmed(100), config: GoogleCalendarConfig, secret: Secret.optional(), settings: CalendarSettingsInput.optional(), reason: Reason }),
]);
export type CreateCalendarConnectionRequest = z.infer<typeof CreateCalendarConnectionRequest>;

export const UpdateCalendarConnectionRequest = z.object({
  expectedVersion: z.number().int().min(1),
  displayName: trimmed(100).optional(),
  config: z.record(z.string(), z.unknown()).optional(),
  /** Write-only: replaces the sealed secret and bumps its version. */
  secret: Secret.optional(),
  settings: CalendarSettingsInput.optional(),
  reason: Reason,
});
export const ConnectionCommandRequest = z.object({ expectedVersion: z.number().int().min(1), reason: Reason });

export interface CalendarSettingsDto {
  readonly autoDraft: boolean;
  readonly internalDomains: readonly string[];
  readonly defaultSiteId: string | null;
  readonly defaultCategoryCode: string | null;
  readonly matchHostByEmail: boolean;
  readonly horizonDays: number;
}

export interface CalendarConnectionSummary {
  readonly id: string;
  readonly provider: CalendarProviderDto;
  readonly adapter: string;
  /** True when the adapter is a local simulator, never a real provider (H-05). */
  readonly simulated: boolean;
  readonly displayName: string;
  readonly status: 'draft' | 'active' | 'disabled';
  readonly config: Readonly<Record<string, unknown>>;
  readonly settings: CalendarSettingsDto;
  readonly secret: { readonly configured: boolean; readonly version: number; readonly updatedAt: string | null };
  readonly testedCurrentVersion: boolean;
  readonly lastTestedAt: string | null;
  readonly lastTestOutcome: 'succeeded' | 'failed' | 'unknown' | null;
  readonly lastTestError: string | null;
  /** Whether the organisation holds the provider's entitlement now. */
  readonly entitled: boolean;
  /** Address providers deliver change notifications to (register it with the provider). */
  readonly notificationUrl: string;
  readonly calendars: number;
  readonly meetingsNeedingAttention: number;
  readonly version: number;
}

export interface CalendarOverviewResponse {
  readonly connections: readonly CalendarConnectionSummary[];
  readonly entitlements: { readonly microsoft: boolean; readonly google: boolean; readonly rooms: boolean };
  /** Adapters available in this deployment per provider; empty = configuration only (H-05). */
  readonly adapters: { readonly microsoft: readonly string[]; readonly google: readonly string[] };
}

export interface CalendarTestResult {
  readonly outcome: 'succeeded' | 'failed' | 'unknown';
  readonly error: string | null;
  readonly connection: CalendarConnectionSummary;
}

// ------------------------------------------------------------------ watched calendars
export const AddCalendarSubscriptionRequest = z.object({
  calendarRef: CalendarRef,
  /** Provider user id of the calendar owner (Entra object id / Google user id), used for identity-link mapping. */
  ownerProviderUserId: z.string().trim().min(1).max(256).regex(/^[A-Za-z0-9._@:/-]+$/, 'v.invalid').optional(),
  /** Administrator mapping when automatic mapping finds no host (recorded as such). */
  hostId: Uuid.optional(),
  reason: Reason,
});

export interface CalendarSubscriptionSummary {
  readonly id: string;
  readonly connectionId: string;
  readonly kind: 'user' | 'room';
  readonly calendarRef: string;
  readonly host: { readonly id: string; readonly displayName: string } | null;
  readonly hostResolution: 'identity_link' | 'external_id' | 'email' | 'administrator' | null;
  readonly room: { readonly locationId: string; readonly name: string; readonly siteId: string } | null;
  readonly status: 'pending' | 'active' | 'expired' | 'failed' | 'disabled';
  readonly expiresAt: string | null;
  readonly needsSync: boolean;
  readonly lastSyncAt: string | null;
  readonly lastSyncOutcome: 'succeeded' | 'failed' | 'unknown' | null;
  readonly lastSyncError: string | null;
  readonly lastFullSyncAt: string | null;
  readonly notifications: number;
  readonly version: number;
}

export interface CalendarSyncResult {
  readonly outcome: 'succeeded' | 'failed' | 'unknown' | 'skipped';
  readonly error: string | null;
  readonly resubscribed: boolean;
  readonly resynced: boolean;
  readonly fetched: number;
  readonly created: number;
  readonly updated: number;
  readonly duplicates: number;
  readonly stale: number;
  readonly drafted: number;
}

export interface CalendarConnectionDetail {
  readonly connection: CalendarConnectionSummary;
  readonly calendars: readonly CalendarSubscriptionSummary[];
  readonly attention: readonly CalendarMeetingSummary[];
}

// ------------------------------------------------------------------ meetings
export type CalendarMappingStatusDto =
  | 'linked'
  | 'no_visitors'
  | 'host_unresolved'
  | 'host_without_account'
  | 'host_not_permitted'
  | 'site_unresolved'
  | 'awaiting_host'
  | 'cancelled'
  | 'not_organizer'
  | 'past'
  | 'unscheduled';

export interface LocalDateTimeDto {
  readonly date: string;
  readonly time: string;
}

export interface MeetingFieldProposal<T> {
  readonly visit: T;
  readonly meeting: T;
}

export interface MeetingReviewDto {
  readonly revision: number;
  readonly title: MeetingFieldProposal<string | null> | null;
  readonly schedule: {
    readonly visit: { readonly startsAt: string | null; readonly endsAt: string | null };
    /** `local` gives the meeting window in the visit's zone, ready for the normal visit update. */
    readonly meeting: { readonly startsAt: string | null; readonly endsAt: string | null; readonly local?: { readonly start: LocalDateTimeDto | null; readonly end: LocalDateTimeDto | null; readonly zone: string } };
  } | null;
  readonly destination: MeetingFieldProposal<{ readonly id: string; readonly name: string } | null> | null;
  readonly added: ReadonlyArray<{ readonly email: string; readonly name: string | null }>;
  /** Impact review for attendees removed in the calendar (PB-04-12). */
  readonly removed: ReadonlyArray<{
    readonly email: string;
    readonly participationId: string;
    readonly displayName: string;
    readonly approvalStatus: string;
    readonly presenceStatus: string;
    readonly invitation: 'active' | 'none';
  }>;
  readonly cancel: boolean;
  /** Applying re-requests approval (material change, PD-026). */
  readonly material: boolean;
}

export interface CalendarMeetingSummary {
  readonly id: string;
  readonly connectionId: string;
  readonly provider: CalendarProviderDto;
  readonly subject: string | null;
  readonly startsAt: string | null;
  readonly endsAt: string | null;
  readonly isAllDay: boolean;
  readonly status: 'confirmed' | 'cancelled';
  readonly organizer: { readonly email: string | null; readonly name: string | null };
  readonly host: { readonly id: string; readonly displayName: string } | null;
  readonly siteId: string | null;
  readonly room: { readonly id: string; readonly name: string } | null;
  /** Visitor addresses: full for the meeting's host, masked for administrators. */
  readonly visitors: ReadonlyArray<{ readonly email: string; readonly name: string | null }>;
  readonly mappingStatus: CalendarMappingStatusDto;
  readonly visit: { readonly id: string; readonly reference: string; readonly planningStatus: string; readonly version: number } | null;
  readonly review: MeetingReviewDto | null;
  readonly revision: number;
  readonly lastModified: string;
  readonly can: { readonly createDraft: boolean; readonly review: boolean };
}

export const MeetingListQuery = z.object({
  scope: z.enum(['mine', 'attention']).default('mine'),
  siteId: Uuid.optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

export const CreateDraftFromMeetingRequest = z.object({
  /** Needed only when neither a linked room, the host's office site nor the connection default decides it. */
  siteId: Uuid.optional(),
  categoryCode: Key.optional(),
});

export const ReviewMeetingRequest = z.object({
  revision: z.number().int().min(1),
  decision: z.enum(['applied', 'dismissed']),
  reason: z.string().trim().max(500, 'v.tooLong').optional(),
});

// ------------------------------------------------------------------ connector / add-in boundary
/** Provider-neutral meeting event a connector may submit (`format: normalised`). */
export const NormalisedMeetingEvent = z.object({
  providerEventId: z.string().trim().min(1).max(512),
  iCalUid: z.string().max(1024).nullable().optional(),
  status: z.enum(['confirmed', 'cancelled']),
  subject: z.string().max(255).nullable().optional(),
  start: z.iso.datetime({ offset: true }).nullable().optional(),
  end: z.iso.datetime({ offset: true }).nullable().optional(),
  isAllDay: z.boolean().optional(),
  location: z.string().max(255).nullable().optional(),
  organizer: z.object({ email: z.string().max(254).nullable().optional(), name: z.string().max(200).nullable().optional(), providerUserId: z.string().max(256).nullable().optional() }),
  attendees: z
    .array(z.object({ email: z.string().max(254), name: z.string().max(200).nullable().optional(), kind: z.enum(['required', 'optional', 'resource']).default('required'), response: z.enum(['accepted', 'declined', 'tentative', 'none']).default('none') }))
    .max(500)
    .default([]),
  changeMarker: z.string().trim().min(1).max(512),
  sequence: z.number().int().min(0).nullable().optional(),
  lastModified: z.iso.datetime({ offset: true }),
});

export const ConnectorEventsRequest = z.object({
  /** How each event is encoded: provider-native JSON (parsed by Passage's adapter) or normalised. */
  format: z.enum(['graph', 'google', 'normalised']),
  /** Watched calendar the events were read from; organiser events of that calendar map to its host. */
  calendarRef: CalendarRef.optional(),
  events: z.array(z.unknown()).min(1).max(100),
});

export interface ConnectorEventsResponse {
  readonly results: ReadonlyArray<{
    readonly providerEventId: string | null;
    readonly outcome: 'created' | 'updated' | 'duplicate' | 'stale' | 'rejected';
    readonly meetingId: string | null;
    readonly mappingStatus: CalendarMappingStatusDto | null;
  }>;
}

// ------------------------------------------------------------------ simulator controls (development/test only)
const SimEvent = z.object({
  eventId: z.string().trim().min(1).max(120).regex(/^[A-Za-z0-9._-]+$/, 'v.invalid').optional(),
  subject: z.string().trim().max(255).optional(),
  start: z.iso.datetime({ offset: true }),
  end: z.iso.datetime({ offset: true }),
  location: z.string().trim().max(255).optional(),
  organizerEmail: z.email('v.email').max(254).optional(),
  attendees: z.array(z.object({ email: z.email('v.email').max(254), name: z.string().max(200).optional(), resource: z.boolean().optional(), response: z.enum(['accepted', 'declined', 'tentative', 'none']).optional() })).max(50).default([]),
});
export const CalendarSimulatorRequest = z.discriminatedUnion('action', [
  z.object({ action: z.literal('upsert_event'), calendarRef: CalendarRef, event: SimEvent }),
  z.object({ action: z.literal('cancel_event'), calendarRef: CalendarRef, eventId: z.string().trim().min(1).max(120) }),
  z.object({ action: z.literal('expire_watch'), calendarRef: CalendarRef }),
  z.object({ action: z.literal('expire_cursor'), calendarRef: CalendarRef }),
  z.object({ action: z.literal('fail_next'), operation: z.enum(['changes', 'subscribe', 'renew', 'write', 'test']), mode: z.enum(['error', 'timeout']) }),
  z.object({ action: z.literal('deliver_notifications'), duplicate: z.boolean().default(false), reverse: z.boolean().default(false) }),
]);
export interface CalendarSimulatorResponse {
  readonly calendars: ReadonlyArray<{ readonly calendarRef: string; readonly events: number; readonly watchExpiresAt: string | null }>;
  readonly pendingNotifications: number;
  readonly delivered: ReadonlyArray<{ readonly status: number }>;
}

// ------------------------------------------------------------------ room calendars (PB-04-13)
export const CreateRoomCalendarLinkRequest = z.object({
  locationId: Uuid,
  connectionId: Uuid,
  resourceRef: CalendarRef,
  mode: z.enum(['two_way', 'publish_only', 'import_only']).default('two_way'),
  externalHolds: z.enum(['block', 'warn']).default('block'),
  reason: Reason,
});
export const UpdateRoomCalendarLinkRequest = z.object({
  expectedVersion: z.number().int().min(1),
  mode: z.enum(['two_way', 'publish_only', 'import_only']).optional(),
  externalHolds: z.enum(['block', 'warn']).optional(),
  status: z.enum(['active', 'disabled']).optional(),
  reason: Reason,
});
export const ResolveRoomConflictRequest = z.object({
  expectedVersion: z.number().int().min(1),
  resolution: z.enum(['reasserted', 'accepted_divergence', 'acknowledged']),
  note: Reason,
});

export interface RoomCalendarLinkSummary {
  readonly id: string;
  readonly location: { readonly id: string; readonly name: string };
  readonly connection: { readonly id: string; readonly displayName: string; readonly provider: CalendarProviderDto; readonly status: string };
  readonly resourceRef: string;
  readonly mode: 'two_way' | 'publish_only' | 'import_only';
  readonly externalHolds: 'block' | 'warn';
  readonly status: 'active' | 'disabled';
  readonly subscription: { readonly status: string; readonly lastSyncAt: string | null; readonly lastSyncOutcome: string | null; readonly expiresAt: string | null };
  readonly lastPublishAt: string | null;
  readonly lastPublishOutcome: 'succeeded' | 'failed' | 'unknown' | 'partial' | null;
  readonly bookings: { readonly synced: number; readonly pending: number; readonly unknown: number; readonly failed: number; readonly conflict: number };
  readonly version: number;
}

export interface RoomExternalBookingDto {
  readonly id: string;
  readonly locationId: string;
  readonly start: string;
  readonly end: string;
  readonly status: 'active' | 'cancelled';
}

export interface RoomSyncConflictDto {
  readonly id: string;
  readonly linkId: string;
  readonly location: { readonly id: string; readonly name: string };
  readonly kind: 'external_overlap' | 'provider_changed' | 'provider_deleted' | 'provider_declined';
  /** Passage's booking and the resource calendar's version, side by side. */
  readonly passage: { readonly start: string; readonly end: string; readonly visitId: string; readonly reference: string } | null;
  readonly provider: { readonly start: string | null; readonly end: string | null; readonly deleted: boolean } | null;
  readonly status: 'open' | 'resolved';
  readonly resolution: 'reasserted' | 'accepted_divergence' | 'acknowledged' | null;
  readonly resolutionNote: string | null;
  readonly createdAt: string;
  readonly version: number;
}

export interface RoomCalendarsResponse {
  readonly siteId: string;
  readonly entitled: boolean;
  readonly rooms: ReadonlyArray<{ readonly id: string; readonly name: string; readonly linkId: string | null }>;
  readonly connections: ReadonlyArray<{ readonly id: string; readonly displayName: string; readonly provider: CalendarProviderDto; readonly status: string }>;
  readonly links: readonly RoomCalendarLinkSummary[];
  readonly conflicts: readonly RoomSyncConflictDto[];
  readonly externalBookings: readonly RoomExternalBookingDto[];
}

export interface RoomSyncResult {
  readonly inbound: CalendarSyncResult;
  readonly published: number;
  readonly removed: number;
  readonly unknown: number;
  readonly failed: number;
  readonly conflicts: number;
}

// ------------------------------------------------------------------ pre-registration links (PB-04-10)
export const IssuePreRegistrationLinkRequest = z.object({ reason: z.string().trim().max(500, 'v.tooLong').optional() });
export const RevokePreRegistrationLinkRequest = z.object({ reason: Reason });

export interface PreRegistrationLinkIssued {
  readonly link: string;
  readonly expiresAt: string;
}

export interface PreRegistrationLinkSummary {
  readonly participationId: string;
  readonly displayName: string;
  readonly approvalStatus: string;
  readonly requirements: number;
  readonly outstanding: number;
  readonly complete: boolean;
  readonly link: { readonly status: 'active' | 'superseded' | 'revoked'; readonly issuedAt: string; readonly expiresAt: string; readonly lastViewedAt: string | null } | null;
  readonly canIssue: boolean;
}

export interface PreRegistrationLinksResponse {
  readonly items: readonly PreRegistrationLinkSummary[];
}

/** What a pre-registration link shows: the checklist and how to get help — nothing that admits entry. */
export interface PreRegistrationLinkView {
  readonly organisation: string;
  readonly site: { readonly name: string; readonly timeZone: string };
  readonly visit: { readonly startsAt: string; readonly endsAt: string; readonly hostName: string };
  readonly visitor: { readonly givenName: string };
  readonly preRegistration: PreRegistrationView;
  readonly assistance: { readonly contactLine: string | null; readonly email: string | null; readonly phone: string | null };
  readonly expiresAt: string;
}
